import type { NotificationCategory, NotificationScope } from "./constants"

export const NOTIFICATION_CATEGORY_COPY: Record<
	NotificationCategory,
	{ label: string; description: string }
> = {
	threads: {
		label: "Threads",
		description: "Customer conversations in your inbox",
	},
	comments: {
		label: "Comments",
		description: "Internal notes from your team",
	},
	subscriptions: {
		label: "Issues, projects and docs",
		description: "Things you follow outside the inbox",
	},
	workspace: {
		label: "Workspace",
		description: "Billing, usage and integrations",
	},
}

export const NOTIFICATION_SCOPE_COPY: Record<
	NotificationScope,
	{ label: string; description: string }
> = {
	off: { label: "Off", description: "Never notify me" },
	involved: {
		label: "Involved",
		description: "When I'm assigned, mentioned or subscribed",
	},
	all: { label: "All", description: "Everything in the workspace" },
}

export const NOTIFICATION_TYPE_COPY: Record<
	string,
	{ label: string; description: string }
> = {
	"thread.created": {
		label: "New thread",
		description: "A customer starts a conversation",
	},
	"thread.reply_received": {
		label: "Customer reply",
		description: "A customer writes back on a thread",
	},
	"thread.reaction_received": {
		label: "Customer reaction",
		description: "A customer reacts to a message on your thread",
	},
	"thread.assigned": {
		label: "Assigned to you",
		description: "Someone gives you a thread",
	},
	"thread.unassigned": {
		label: "Unassigned from you",
		description: "A thread moves away from you",
	},
	"thread.done": {
		label: "Marked done",
		description: "A thread you follow is closed",
	},
	"thread.reopened": {
		label: "Reopened",
		description: "A closed thread opens again",
	},
	"thread.ai_draft_ready": {
		label: "AI draft ready",
		description: "The agent wrote a reply for you to review",
	},
	"thread.ai_handoff": {
		label: "Handed to your team",
		description: "The agent passes a conversation to a person",
	},
	"thread.ai_resolved": {
		label: "Resolved by the agent",
		description: "The agent solved a conversation and marked it done",
	},
	"thread.snooze_ended": {
		label: "Thread unsnoozed",
		description: "A snoozed thread returns to the inbox",
	},
	"thread.sla_at_risk": {
		label: "SLA at risk",
		description: "A reply is due soon",
	},
	"thread.sla_breached": {
		label: "SLA breached",
		description: "A reply is overdue",
	},
	"thread.topic_added": {
		label: "Thread joins something you follow",
		description: "A thread gets a tag or company you're subscribed to",
	},
	"thread.linked_issue_completed": {
		label: "Linked issue done",
		description: "An issue linked to the thread ships",
	},
	"comment.mentioned": {
		label: "Mentions",
		description: "Someone mentions you in a comment",
	},
	"comment.created": {
		label: "Comments",
		description: "Someone comments on a thread you follow",
	},
	"issue.request_linked": {
		label: "New request on an issue",
		description: "A customer thread links to an issue you follow",
	},
	"issue.status_changed": {
		label: "Issue status",
		description: "An issue you follow changes status",
	},
	"project.request_linked": {
		label: "New request on a project",
		description: "A customer thread links to a project you follow",
	},
	"project.status_changed": {
		label: "Project status",
		description: "A project you follow changes status",
	},
	"docs.proposal_ready": {
		label: "Docs suggestion",
		description: "The agent proposes a change to an article",
	},
	"docs.api_sync_held": {
		label: "API reference held back",
		description: "A daily sync finds breaking changes",
	},
	"docs.api_sync_failed": {
		label: "API reference sync failed",
		description: "The daily sync can't read your spec",
	},
	"billing.payment_failed": {
		label: "Payment failed",
		description: "A payment for the workspace didn't go through",
	},
	"ai.cap_reached": {
		label: "AI usage limit",
		description: "AI usage nears or reaches its monthly cap",
	},
	"slack.broadcast_disconnected": {
		label: "Slack disconnected",
		description: "The workspace Slack connection stops working",
	},
	"slack.personal_dm_failed": {
		label: "Slack DMs stopped",
		description: "Productlane can't reach your Slack anymore",
	},
	"import.zendesk_finished": {
		label: "Import finished",
		description: "Your Zendesk import is done",
	},
	"import.zendesk_failed": {
		label: "Import failed",
		description: "Your Zendesk import stopped with an error",
	},
}

/** Notification titles rendered at fan-out time and stored as a snapshot. */
export const notificationTitles = {
	threadCreated: (from: string) => `New thread from ${from}`,
	replyReceived: (from: string) => `${from} replied`,
	reactionReceived: (from: string, emoji: string) => `${from} reacted ${emoji}`,
	assigned: (actor: string | null, customer: string | null) => {
		const thread = customer ? `a thread with ${customer}` : "a thread"
		return actor
			? `${actor} assigned you ${thread}`
			: `You were assigned to ${thread}`
	},
	unassigned: (actor: string | null) =>
		actor ? `${actor} unassigned you` : "You were unassigned",
	done: (actor: string | null) =>
		actor ? `${actor} marked a thread done` : "Thread marked done",
	reopened: () => "Thread reopened",
	aiDraftReady: () => "AI draft ready to review",
	aiHandoff: () => "The agent handed a thread to your team",
	aiResolved: () => "The agent resolved a thread",
	snoozeEnded: () => "Thread unsnoozed",
	slaAtRisk: () => "Reply due soon",
	slaBreached: () => "Reply overdue",
	topicAdded: (topic: string) => `Thread added to ${topic}`,
	linkedIssueCompleted: (issue: string) => `${issue} is done`,
	mentioned: (actor: string | null) =>
		actor ? `${actor} mentioned you` : "You were mentioned",
	commented: (actor: string | null) =>
		actor ? `${actor} commented` : "New comment",
	requestLinked: (target: string) => `New customer request on ${target}`,
	statusChanged: (target: string, status: string) =>
		`${target} moved to ${status}`,
	docsProposalReady: (article: string | null) =>
		article ? `Suggested update to ${article}` : "New docs suggestion",
	slackPersonalDmFailed: () => "Slack notifications stopped",
	slackBroadcastDisconnected: () => "Slack stopped receiving new threads",
	slackBroadcastDisconnectedBody: () =>
		"Reconnect Slack to post new threads to your channel again",
	zendeskFinished: () => "Zendesk import finished",
	zendeskFailed: () => "Zendesk import failed",
	fallbackActor: "Someone",
	fallbackCustomer: "a customer",
	untitledThread: "Untitled thread",
}
